import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { WebAuthn } from '#api';
import { trackError } from '#api/errors.ts';
import { isPublicKeyCredential, isWebAuthnAbort, isWebAuthnCancellation } from '#api/webauthn.ts';
import { useAuth } from '#global/context/auth/useAuth.tsx';

export function useWebAuthnLogin({ onSuccess }: { onSuccess: () => void }) {
  const auth = useAuth();
  const [webAuthnError, setWebAuthnError] = useState<string>();
  const abortRef = useRef<AbortController | undefined>(undefined);

  const webAuthnMutation = useMutation({
    mutationFn: async () => {
      const abortController = new AbortController();
      abortRef.current = abortController;

      const { walsToken, credentialAssertion } = await WebAuthn.beginAuthentication();
      const assertionOptions = WebAuthn.prepareAssertionOptions(credentialAssertion);

      // The browser/password-manager prompt stays open until the user acts on it.
      // Tie it to this hook's lifetime so a prompt left open on a screen the user
      // navigated away from (e.g. by submitting the magic-link form instead) can't
      // complete later and log them in from a page that no longer reacts to it.
      const credential = await navigator.credentials.get({
        ...assertionOptions,
        signal: abortController.signal,
      });
      if (!isPublicKeyCredential(credential)) {
        throw new Error('No credential was returned from the browser.');
      }

      const serialized = WebAuthn.serializeAssertion(credential);
      const response = await WebAuthn.finalizeAuthentication({
        walsToken,
        credentialAssertionResponse: serialized,
      });

      await auth.loginWithWebAuthn(response);
    },
    onSuccess,
    onError: (err) => {
      if (isWebAuthnAbort(err)) {
        // Aborted by unmount; there is no screen left to show an error on.
        return;
      }
      if (
        isWebAuthnCancellation(err) ||
        (err instanceof Error && err.message === 'No credential was returned from the browser.')
      ) {
        setWebAuthnError('The request was cancelled or timed out.');
      } else {
        trackError(err);
        setWebAuthnError('Could not authenticate with passkey. Please try another method.');
      }
    },
  });

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  function clearError() {
    setWebAuthnError(undefined);
  }

  return { webAuthnMutation, webAuthnError, clearError };
}
